import {
  all,
  fork,
  takeLatest,
  takeLeading,
  put,
  call,
  race,
  take,
  select,
  delay,
} from 'typed-redux-saga/macro';
import { find, isEmpty } from 'lodash';
import { modules } from '@veritone/glc-redux';
import { IN_SET_AUTH, IN_SET_CONFIG, AuthResponse } from '@worker';
import { redirect } from 'redux-first-router';

import * as Sentry from '@sentry/browser';

import {
  ROUTE_AUTH,
  ROUTE_LOGOUT,
  selectRouteType,
  selectCurrentRoutePayload,
  PUBLIC_STANDALONE_ROUTE_TYPES,
} from '../routing';
import {
  BOOT,
  bootFinished,
  boot,
  BOOT_FINISHED,
  selectIsApplicationConfigsLoaded,
} from './index';
import {
  FETCH_APPLICATION_CONFIGS_FAILURE,
  FETCH_APPLICATION_CONFIGS_SUCCESS,
} from './actions';
import { selectConfig } from '@common-modules/engines/selectors';
import { AnyAction } from '@reduxjs/toolkit';
import { Action } from '@redact/state/modules/casemedia/models';
import { queryApplicationConfigsService } from './services';
import { clearAllCachedSchemaIds } from '@common-modules/notification/services';
import { FALLBACK_VERITONE_APP_ID } from '@helpers/constants';

const {
  user: {
    fetchUser,
    fetchEnabledApps,
    FETCH_USER,
    FETCH_USER_SUCCESS,
    FETCH_USER_FAILURE,
    FETCH_USER_APPLICATIONS,
    FETCH_USER_APPLICATIONS_SUCCESS,
    FETCH_USER_APPLICATIONS_FAILURE,
    LOGOUT,
    LOGOUT_FAILURE,
    LOGOUT_SUCCESS,
    selectUser,
  },
  auth: { setOAuthToken, OAUTH_GRANT_FLOW_SUCCESS },
  config: { getConfig },
} = modules;

export const getAppStartupDependencies = function* () {
  // fetch stuff
  yield* all([
    put(fetchEnabledApps()),
    // ...other app dependencies
  ]);

  // wait for results
  const actions = [
    ...(yield* race([
      take(FETCH_USER_APPLICATIONS_SUCCESS),
      take([
        // requestError
        // TODO: Fix this deprecated type
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        (a: AnyAction) => a.type === FETCH_USER_APPLICATIONS && !!a.error,
        // api error
        FETCH_USER_APPLICATIONS_FAILURE,
      ]),
    ])),
    // ...etc
  ];

  // fixme -- refactor FETCH_USER/FETCH_USER_APPLICATIONS in redux-common to thunk style
  // and graphql, then replace this ugly take block and use put.resolve

  const error = find(actions, { error: true });
  if (error) {
    console.error('there was an error', error);
  }
};

function* watchAppBoot() {
  yield* takeLatest(BOOT, watchAppBootHandle);
}

export function* watchAppBootHandle() {
  const config = yield* select(getConfig<Window['config']>);

  // Public standalone routes (e.g. the popped-out keyboard-shortcuts legend)
  // render static content with no auth. Skip the user fetch / startup network
  // calls and never redirect to login, so the window loads fast and stays open
  // even after the user logs out.
  const routeType = yield* select(selectRouteType);
  if (PUBLIC_STANDALONE_ROUTE_TYPES.includes(routeType)) {
    yield* put(IN_SET_CONFIG(config));
    yield* put(bootFinished());
    return;
  }

  const user = yield* fetchUserWithStoredTokenOrCookie();

  // yield take(OUT_WORKER_READY);
  yield* put(IN_SET_CONFIG(config));

  if (user) {
    yield* put(IN_SET_AUTH(user));
    // login success with stored credentials or cookie
    yield* getAppStartupDependencies();
    yield* put(bootFinished());

    Sentry.setUser({
      id: user.userId,
      email: user.email ?? '',
    });
  } else {
    if (config.useOAuthGrant) {
      yield* redirectAndAwaitOAuthGrant();
    } else {
      yield* redirectToVeritoneInternalLogin();
    }
  }
}

export const redirectAndAwaitOAuthGrant = function* () {
  const routeType = yield* select(selectRouteType);
  const routePayload = yield* select(selectCurrentRoutePayload);

  if (routeType !== ROUTE_AUTH.type) {
    yield* put(
      ROUTE_AUTH({
        query: {
          nextType: routeType,
          nextPayload: !isEmpty(routePayload)
            ? JSON.stringify(routePayload)
            : undefined,
        },
      })
    );
  }

  yield* put(bootFinished());

  // retry boot after logging in
  yield* take(OAUTH_GRANT_FLOW_SUCCESS);
  yield* put(boot());
};

export const redirectToVeritoneInternalLogin = function* () {
  const config = yield* select(getConfig);
  if (config.loginRoute) {
    const loginRoute = new URL(config.loginRoute);
    if (loginRoute.hostname.match(/\.veritone\.com$/)) {
      const redirect = new URL(window.location.href);
      if (redirect.hostname.match(/\.veritone\.com$/)) {
        loginRoute.searchParams.set('redirect', redirect.href);
        window.location.href = loginRoute.href;
      } else {
        window.location.href = loginRoute.href;
      }
    }
  }
};

export const fetchUserWithStoredTokenOrCookie = function* () {
  const existingOAuthToken = yield* call(
    [localStorage, 'getItem'],
    'OAuthToken'
  );

  if (existingOAuthToken) {
    yield* put(setOAuthToken(existingOAuthToken));
  }

  yield* put(fetchUser());

  const { successAction } = yield* race({
    successAction: take<Action<AuthResponse>>(FETCH_USER_SUCCESS),
    _: take([
      // TODO: Fix deprecated type
      // eslint-disable-next-line @typescript-eslint/no-deprecated, @typescript-eslint/no-unsafe-return
      (a: AnyAction) => a.type === FETCH_USER && a.error,
      FETCH_USER_FAILURE,
    ]),
  });

  // todo: this could differentiate between auth error (expired token) and failure
  // (api error)
  return successAction ? successAction.payload : false;
};

function* storeTokenAfterSuccessfulOAuthGrant() {
  yield* takeLatest(
    OAUTH_GRANT_FLOW_SUCCESS,
    // TODO: Can we type the imported actions?
    function* ({ payload: { OAuthToken } }: any) {
      yield* call([localStorage, 'setItem'], 'OAuthToken', OAuthToken);
    }
  );
}

function* clearStoredTokenAfterLogout() {
  yield* takeLatest(LOGOUT, clearStoredTokenAfterLogoutHandle);
}

/**
 * Resolved schema ids are cached in module scope with no expiry, so the only
 * things that drop a superseded id are an explicit invalidation and these two
 * events.
 *
 * LOGOUT is defence in depth: the cache dies with the document on the
 * `window.location.assign` at the end of the logout handle below, but
 * `redirectAndAwaitOAuthGrant` re-boots in-process without a navigation, and one
 * user's resolved ids must not survive into another's session.
 *
 * BOOT is what bounds staleness in practice. Without it a tab left open for days
 * holds its ids for the life of the *document*; with it the bound is the life of
 * a *session*, and the in-process re-auth path re-boots too. It costs nothing —
 * at first boot the cache is already empty.
 */
function* clearSchemaIdCacheOnSessionChange() {
  yield* takeLatest([LOGOUT, BOOT], function* () {
    yield* call(clearAllCachedSchemaIds);
  });
}

export function* clearStoredTokenAfterLogoutHandle() {
  const user = yield* select(selectUser);
  if (!isEmpty(user)) {
    yield* call([localStorage, 'removeItem'], 'OAuthToken');
    yield* put(redirect(ROUTE_LOGOUT()));
    yield* take([LOGOUT_FAILURE, LOGOUT_SUCCESS]);
    yield* delay(1000);
    const { loginRoute } = yield* select(selectConfig);
    try {
      const loginUrl = new URL(loginRoute);
      loginUrl.searchParams.set('redirect', window.location.origin);
      window.location.assign(loginUrl.href);
    } catch {
      console.warn('Warning: Login route is not a valid url');
    }
  }
}

// Bounded backoff for the org application-config fetch. Five attempts over ~1 minute:
// long enough to ride out a transient GraphQL blip, short enough that a genuinely broken
// endpoint stops being hammered.
export const APPLICATION_CONFIGS_RETRY_DELAYS_MS = [
  1_000, 3_000, 8_000, 20_000, 30_000,
] as const;

export function* fetchApplicationConfigs() {
  const config = yield* select(getConfig<Window['config']>);
  yield* put(
    queryApplicationConfigsService(
      config.veritoneAppId || FALLBACK_VERITONE_APP_ID
    )
  );
}

function* watchAppBootFinished() {
  yield* takeLatest(BOOT_FINISHED, fetchApplicationConfigs);
}

/**
 * Re-fetch the org application config when its query fails.
 *
 * That config decides trash-vs-permanent delete (`trashRetentionDays`) and supplies the
 * merged feature flags, and its failure used to be dispatched as `'noop'` — swallowed
 * entirely. That was survivable only because of an accident: every side-menu click was a
 * full page reload, which re-ran boot and re-fetched the config, so a transient failure
 * self-healed within a navigation or two. VE-26729 makes navigation in-app, so boot runs
 * once per tab and the failure would otherwise stand for the whole session, leaving
 * `trashRetentionDays` at its 0 fallback the entire time.
 *
 * This restores the self-healing the reload used to provide, without restoring the reload.
 * It is strictly cheaper than master, which re-fetched on every navigation: this only
 * fetches again after a failure, and stops as soon as the config lands (or the attempts run
 * out). Deliberately silent — the retry either succeeds, in which case there was nothing to
 * tell the user, or exhausts, in which case Sentry carries it.
 */
export function* retryApplicationConfigsOnFailureHandle() {
  for (const delayMs of APPLICATION_CONFIGS_RETRY_DELAYS_MS) {
    yield* delay(delayMs);
    // A concurrent attempt may have landed while we waited.
    if (yield* select(selectIsApplicationConfigsLoaded)) {
      return;
    }
    yield* call(fetchApplicationConfigs);
    const { ok } = yield* race({
      ok: take(FETCH_APPLICATION_CONFIGS_SUCCESS.type),
      failed: take(FETCH_APPLICATION_CONFIGS_FAILURE.type),
    });
    if (ok) {
      return;
    }
  }
  Sentry.captureMessage(
    'Application config fetch failed after all retries; trashRetentionDays and org feature flags stay at their fallbacks for this session',
    'error'
  );
}

export function* retryApplicationConfigsOnFailure() {
  // takeLeading, NOT takeLatest: the ladder's own failed retries dispatch this same
  // action (it is what the handle's internal race is waiting on), and takeLatest would
  // consume each one to cancel the in-flight handle and re-fork it at the first rung —
  // an endless 1s loop that never reaches the longer rungs or the give-up above. With
  // takeLeading the watcher stays blocked while the handle runs, so a retry's failure
  // only resolves the race and advances the ladder; a fresh ladder can start only after
  // the previous one has finished.
  yield* takeLeading(
    FETCH_APPLICATION_CONFIGS_FAILURE.type,
    retryApplicationConfigsOnFailureHandle
  );
}

export default function* auth() {
  yield* all([
    fork(watchAppBoot),
    fork(storeTokenAfterSuccessfulOAuthGrant),
    fork(clearStoredTokenAfterLogout),
    fork(clearSchemaIdCacheOnSessionChange),
    fork(watchAppBootFinished),
    fork(retryApplicationConfigsOnFailure),
  ]);
}
